import type { PublishStatus } from '@/api/types';

/**
 * 中文文案集中常量（Copy Catalog，F01）。
 *
 * 业务组件 `import { copy } from '@/i18n/zh-CN'` 取 key 渲染，
 * 不允许在组件内写死字符串。一期只做中文，二期切到 vue-i18n
 * 时把本文件按 key 拆为 i18n 资源即可。
 *
 * `PublishStatus` 引用 `@/api/types` 本地 wire 类型（F02 落地，
 * 沿用后端 *View 约定，camelCase 字段名与后端集成测试钉死的契约
 * 一致）；F01 阶段曾消费共享包 `@edu/shared/api-types` 占位，F02
 * review 修复时切到本地真实类型避免两份来源并存。
 */

/** 当前唯一已知的发布态文案映射；后续 F08 Preview Mode 渲染 Draft Badge 时复用。 */
export const publishStatusLabel: Readonly<Record<PublishStatus, string>> = {
  Draft: '草稿',
  Published: '已发布',
};

export const copy = {
  app: {
    title: '教学成果展示',
    /** HomePage 加载态骨架屏文案（F03） */
    loading: '加载中...',
  },

  /** 首页锚点导航（F04 / F13 全局 navbox） */
  anchorNav: {
    label: '页面导航',
    /** F13 targetAnchor 非法时可见的错误提示（Debug-First：不静默无响应）。 */
    invalidTarget: '该导航项配置有误，暂时无法跳转',
  },

  /** F05 Section 组件共享文案 */
  sections: {
    empty: '暂无内容',
    material: {
      sizeUnknown: '大小未知',
      typeUnknown: '类型未知',
    },
    /** F11 BannerSection 轮播控件文案（占位 `N` 由组件按 dot 序号替换）。 */
    banner: {
      /** 空 / null images 时 Section-level Failure 占位卡提示。 */
      dataMissing: 'Banner 数据缺失',
      prevAria: '上一张',
      nextAria: '下一张',
      dotAria: '第 N 张',
    },
    /** F14 科研机构区文案（消费 SiteConfig.researchInstitutions）。 */
    institutions: {
      /** 区标题（对齐模板 .nyZzj dt「科研机构」）。 */
      title: '科研机构',
    },
  },

  /** Fallback Notice 三级文案（前台 ADR-0005 / 容错与降级） */
  fallback: {
    /** Aggregate Endpoint 整体失败 → 整页 Banner */
    critical: {
      title: '页面加载失败',
      description: '内容服务暂时不可用，请稍后重试。',
      /** 重试按钮无障碍标签（F07 CriticalFailureBanner）。 */
      retryAria: '重新加载页面内容',
    },
    /** 单 Section 字段缺失 / type 未注册 / 渲染抛错 */
    section: {
      title: '该模块暂不可用',
      unknownType: '此内容类型暂未支持。',
      /** Section 渲染抛错（SectionErrorBoundary 捕获）时的占位提示。 */
      renderFailed: '该模块渲染异常，请稍后重试。',
      placeholderHint: '占位组件，F5 替换为真实实现',
      typeLabel: '类型',
    },
    /** 单图 / 视频 / 材料加载失败 */
    asset: {
      imageFailed: '图片加载失败',
      /** 图片占位 aria-label（F07 Asset-level）。 */
      imageFailedAria: '图片加载失败',
      videoUnavailable: '视频暂不可用',
      materialUnavailable: '附件暂不可用',
    },
    /** 网络层失败（fetch 抛错） */
    network: '网络异常，请检查连接后重试。',
    /** 详情页路由参数定位不到 Section（F10 起承接） */
    detail: {
      title: '内容不存在',
      description: '该内容可能已被移除或尚未发布。',
    },
  },

  buttons: {
    retry: '重试',
    backHome: '返回首页',
    download: '下载',
    previewPdf: '在线预览',
    closePreview: '关闭预览',
    expand: '展开',
    collapse: '收起',
    openInNewTab: '新标签打开',
  },

  /** F25 图片灯箱（ImageLightbox）操作栏与无障碍文案。 */
  lightbox: {
    dialogAria: '图片查看',
    zoomIn: '放大',
    zoomOut: '缩小',
    rotateLeft: '向左旋转',
    rotateRight: '向右旋转',
    reset: '重置',
    close: '关闭',
  },

  /** Preview Mode 文案（F08 Preview Banner / DraftBadge / 过期 UX 复用） */
  preview: {
    bannerTitle: '预览模式',
    bannerDescription: '当前页面正在预览未发布的草稿内容，仅管理员可见。',
    /** 横幅「返回管理端」按钮文案（跳 `/admin/`，非前台路由）。 */
    backToAdmin: '返回管理端',
    /** token 过期 / 失效时的专属文案（CriticalFailureBanner 过期分支消费）。 */
    tokenExpired: '预览已过期，请回到管理端重新生成预览链接。',
    draftBadge: '草稿',
  },

  /** 首页 grid 区标题兜底（F12 `resolveGroupTitle` 在 NavItem 缺失时消费） */
  sectionGroupTitles: {
    problem: '教学问题',
    method: '解决方法',
    innovation: '成果创新点',
    impact: '推广应用效果',
    video: '成果视频',
    'bottom-video': '视频板块',
  },

  notFound: {
    title: '页面不存在',
    description: '您访问的页面已被移除或地址有误。',
    backHome: '返回首页',
  },

  /**
   * Stable Error Code → 用户可见文案映射表（F02 error-mapper 消费）。
   *
   * key 对齐后端 `Domain.ErrorCodes` 的稳定字符串（`system.unexpected` /
   * `validation.failed` / `section.not_found` 等，后端 ADR-0008 约定改名
   * 视为破坏性变更）。`network.error` 对应前台 NetworkError（无后端
   * errorCode）。`__unknown` 兜底未识别码——error-mapper 命中未知码时
   * console.error 暴露 + 返此项（Debug-First，不静默返默认）。
   */
  errorCodes: {
    'system.unexpected': {
      title: '页面加载失败',
      description: '内容服务暂时不可用，请稍后重试。',
    },
    'validation.failed': {
      title: '页面加载失败',
      description: '请求参数校验未通过，请稍后重试。',
    },
    'section.not_found': {
      title: '该模块暂不可用',
      description: '此内容可能已被移除或尚未发布。',
    },
    'network.error': {
      title: '网络异常',
      description: '网络异常，请检查连接后重试。',
    },
    __unknown: {
      title: '页面加载失败',
      description: '发生未知错误，请稍后重试。',
    },
  } as const,

  detail: {
    placeholderTitle: '详情页脚手架就绪',
    /** 首页 Section 卡片「更多+」入口文案（F10 共享 DetailMoreLink）。 */
    more: '更多+',
    /** 详情页左侧目录导航标题（对齐模板 sidebar-title「目录导航」）。 */
    sidebarTitle: '目录导航',
    /** 成果视频专栏名称。 */
    achievementVideo: '成果视频',
    /** Detail-level Fallback 返回首页按钮（复用 buttons.backHome，此处留独立键供详情页专用文案演进）。 */
    backHome: '返回首页',
    /** 详情页附件区标题（对齐模板 .section-title，F10 MaterialList）。 */
    materialTitle: '支撑材料',
  },

  /** F17 全局 chrome 文案（SiteHeader / SiteFooter，首页 + 详情页共享）。 */
  chrome: {
    header: {
      /** siteConfig.title 缺失（idle / failed）时的兜底标题。 */
      titleFallback: '教学成果展示',
      /** logo `<img alt>` 文案（学校名）。 */
      logoAlt: '琼台师范学院',
    },
  },
} as const;

export type Copy = typeof copy;
